import { Link } from "react-router";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { Kicker } from "./Kicker";
import { fadeIn } from "./fadeIn";

/**
 * Want vs need. The hero states the want as an outcome ("know if you've been
 * hit"); this section earns the need (evidence, including what was never
 * logged, and who approved what). We give people the want by delivering the need.
 */
const ROWS: Array<{ who: string; want: string; need: string; href?: string; cta?: string }> = [
  {
    who: "SOC and hunt leads",
    want: "A calm answer when the CISO forwards the advisory. Fewer consoles. No lost weekend.",
    need: "Evidence behind the answer, including what wasn't logged, and coverage of the threats in the news.",
    href: "/solutions/soc",
    cta: "For SOC teams",
  },
  {
    who: "CISOs",
    want: "To say “we checked” and mean it.",
    need: "Proof for the board, auditors and regulators: what was checked, what was found, and who approved each action.",
    href: "/trust-center",
    cta: "Trust center",
  },
  {
    who: "MSSPs and IR firms",
    want: "To serve more clients with the same team.",
    need: "A defensible record of every hunt and action, kept separately for each client.",
    href: "/solutions/mssp",
    cta: "For MSSPs",
  },
];

export function WantNeedSection() {
  const reduce = useReducedMotion();
  return (
    <section className="bg-card px-6 py-10 md:px-12 md:py-28" aria-labelledby="want-need-heading">
      <div className="mx-auto flex max-w-7xl flex-col gap-8 md:gap-12">
        <motion.div {...fadeIn(reduce)} className="flex max-w-3xl flex-col gap-4 md:gap-5">
          <Kicker>What you want, and what you need</Kicker>
          <h2 id="want-need-heading" className="heading-lg text-ink text-balance">
            You want the answer. You&apos;re accountable for the evidence.
          </h2>
          <p className="hidden text-lg leading-relaxed text-ink-muted md:block">
            Huntbase gives you both: the same-day answer, and the record behind it.
          </p>
        </motion.div>

        <div className="hidden grid-cols-[1fr_2fr_2fr] gap-6 border-b border-ink pb-3 text-sm font-semibold text-ink-subtle md:grid">
          <span>Who</span>
          <span>What they want</span>
          <span>What they need</span>
        </div>
        <ul className="-mt-4 flex flex-col divide-y divide-rule md:-mt-12">
          {ROWS.map((r, i) => (
            <motion.li
              key={r.who}
              {...fadeIn(reduce, 0.06 * i)}
              className="grid gap-2 py-5 md:grid-cols-[1fr_2fr_2fr] md:gap-6 md:py-8"
            >
              <div className="flex flex-col gap-1">
                <h3 className="heading-sm text-ink">{r.who}</h3>
                {r.href && (
                  <Link
                    to={r.href}
                    className="group hidden items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-500 md:inline-flex"
                  >
                    {r.cta}
                    <ArrowRight
                      size={14}
                      weight="bold"
                      aria-hidden
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </Link>
                )}
              </div>
              <p className="text-[15px] leading-relaxed text-ink md:text-base">
                <span className="font-semibold text-ink md:hidden">Want: </span>
                {r.want}
              </p>
              <p className="text-[15px] leading-relaxed text-ink md:text-base">
                <span className="font-semibold text-primary-600 md:hidden">Need: </span>
                {r.need}
              </p>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
